import React from 'react';
import { Col, Row, Radio, Input } from 'antd';
import { Color } from 'services';
import { docButton } from './documentForm';
import { formatGst } from './businessInfoForm';

// Persisted verbatim in kyc.gst_exemption_type - keep these strings in sync
// with the backend enum rather than retyping the literals at each call site.
export const GST_EXEMPTION_GOVT = "Govt Affiliated";
export const GST_EXEMPTION_TURNOVER = "Turnover Below 20 Lakhs";
export const GST_EXEMPTION_EDUCATIONAL = "Educational Institution";

// Persisted in kyc.gst_education_category. These are the three limbs of the
// "educational institution" definition that the GST exemption turns on, so an
// institute claiming that reason has to say which one it qualifies under.
export const GST_EDU_PRESCHOOL_TO_HSC = "Preschool to Higher Secondary";
export const GST_EDU_RECOGNISED_QUALIFICATION = "Recognised Qualification Curriculum";
export const GST_EDU_VOCATIONAL = "Approved Vocational Course";

const educationCategories = [
    { value: GST_EDU_PRESCHOOL_TO_HSC, label: "Preschool education through higher secondary education" },
    { value: GST_EDU_RECOGNISED_QUALIFICATION, label: "Education as part of a curriculum for a qualification recognised by law" },
    { value: GST_EDU_VOCATIONAL, label: "Approved vocational education course" },
];

const proofTitle = (exemptionType) => (
    exemptionType === GST_EXEMPTION_GOVT ? "Government Affiliation Proof" :
        exemptionType === GST_EXEMPTION_TURNOVER ? "Turnover Proof" :
            "Education Recognition Proof"
);

const proofHint = (exemptionType, educationCategory) => {
    if (exemptionType === GST_EXEMPTION_GOVT) {
        return "Upload the affiliation certificate or government order establishing the affiliation.";
    }
    if (exemptionType === GST_EXEMPTION_TURNOVER) {
        return "Upload a document evidencing an annual turnover below 20 lakhs.";
    }
    if (educationCategory === GST_EDU_PRESCHOOL_TO_HSC) {
        return "Upload the board affiliation or recognition certificate covering the levels taught.";
    }
    if (educationCategory === GST_EDU_VOCATIONAL) {
        return "Upload the approval document for the vocational education course.";
    }
    return "Upload the document recognising the qualification under law.";
}

export const gstExemption = (kycDetails, handleUpdateDetails, changeGstExempted, changeGstExemptionType,
    changeGstEducationCategory, handleUploadChange, beforeUpload, handleDocClick, handleEditCommentClick) => {
    const locked = kycDetails.kycStatus === "Submitted" || kycDetails.kycStatus === "Approved";
    const isEducational = kycDetails.gstExemptionType === GST_EXEMPTION_EDUCATIONAL;
    // The educational route needs its sub-category settled before the proof
    // means anything - which document to ask for depends on which limb applies.
    const showProof = kycDetails.gstExemptionType && (!isEducational || kycDetails.gstEducationCategory);
    return (
        <div style={{ width: "100%" }}>
            <Row className="m-t-20">
                <Col xs={{ span: 24 }} md={{ span: 12, offset: 0 }}>
                    <div className='text-xs color-black m-b-5 bold-600'>
                        Are you exempted from GST? <span style={{ color: Color.red }}>*</span>
                    </div>
                    <div className="display-flex" style={{ height: 50, alignItems: 'center' }}>
                        <Radio.Group
                            disabled={locked}
                            onChange={(e) => changeGstExempted(e)}
                            value={kycDetails.gstExempted}
                        >
                            <Radio value={true}>Yes</Radio>
                            <Radio value={false}>No</Radio>
                        </Radio.Group>
                    </div>
                </Col>
            </Row>

            {kycDetails.gstExempted === false ? (
                <>
                    <div className="text-xs m-t-10 dark-grey">
                        GST at 18% will be applicable on your transactions.
                    </div>
                    {/* same gstNo as Business Details - GST is charged only with a GSTIN */}
                    <Row className="m-t-20">
                        <Col xs={{ span: 24 }} md={{ span: 12, offset: 0 }}>
                            <div className='text-xs color-black m-b-5 bold-600'>
                                GSTIN <span style={{ color: Color.red }}>*</span>
                            </div>
                            <Input
                                onChange={(e) => {
                                    const val = formatGst(e.target.value);
                                    if (val.length <= 15) {
                                        handleUpdateDetails(true, 'gstAvailable');
                                        handleUpdateDetails(val, 'gstNo');
                                    }
                                }}
                                value={kycDetails.gstNo}
                                placeholder="Enter GSTIN number"
                                className="radius-5 kyc-input-fields"
                                disabled={locked}
                                style={{ background: Color.lightGrey, border: "none" }}
                            />
                            {kycDetails.gstValid === false ? (
                                <div className="text-xs red bold-600">Enter a valid GSTIN (e.g., 22ABCDE1234F1Z5).</div>
                            ) : !kycDetails.gstNo ? (
                                <div className="text-xs m-t-5 dark-grey">A GSTIN is required as you are not exempted from GST.</div>
                            ) : null}
                        </Col>
                    </Row>
                </>
            ) : null}

            {kycDetails.gstExempted === true ? (
                <Row className="m-t-20">
                    <Col xs={{ span: 24 }} md={{ span: 24, offset: 0 }}>
                        <div className='text-xs color-black m-b-5 bold-600'>
                            Reason for exemption <span style={{ color: Color.red }}>*</span>
                        </div>
                        <div className="m-t-10">
                            <Radio.Group
                                disabled={locked}
                                onChange={(e) => changeGstExemptionType(e.target.value)}
                                value={kycDetails.gstExemptionType}
                            >
                                <Radio value={GST_EXEMPTION_GOVT}>Govt affiliated</Radio>
                                <Radio value={GST_EXEMPTION_TURNOVER}>Less than 20 lakhs per annum</Radio>
                                <Radio value={GST_EXEMPTION_EDUCATIONAL}>Educational institution under GST law</Radio>
                            </Radio.Group>
                        </div>
                    </Col>
                </Row>
            ) : null}

            {isEducational ? (
            <Row className="m-t-20">
                <Col xs={{ span: 24 }} md={{ span: 24, offset: 0 }}>
                    <div className='text-xs color-black m-b-5 bold-600'>
                        Which category applies? <span style={{ color: Color.red }}>*</span>
                    </div>
                    <div className="text-xs m-b-10 dark-grey">
                        GST law defines an educational institution for the exemption as an institution
                        providing any of the following.
                    </div>
                    <Radio.Group
                        disabled={locked}
                        onChange={(e) => changeGstEducationCategory(e.target.value)}
                        value={kycDetails.gstEducationCategory}
                    >
                        {educationCategories.map(category => (
                            <Radio
                                key={category.value}
                                value={category.value}
                                style={{ display: 'block', height: 30, lineHeight: '30px' }}
                            >
                                {category.label}
                            </Radio>
                        ))}
                    </Radio.Group>
                </Col>
            </Row>
            ) : null}

            {kycDetails.gstExempted === true && showProof ? (
                <Row className="m-t-20">
                    <Col xs={{ span: 24 }} md={{ span: 12, offset: 0 }}>
                        {docButton(proofTitle(kycDetails.gstExemptionType), true, kycDetails, handleUploadChange,
                            beforeUpload, 'gst_exemption_proof', handleUpdateDetails, handleDocClick, handleEditCommentClick)}
                        <div className="text-xs m-t-10 dark-grey">
                            {proofHint(kycDetails.gstExemptionType, kycDetails.gstEducationCategory)}
                        </div>
                        <div className="text-xs m-t-10 dark-grey">Upload JPG, PNG, or PDF files (max 5 MB each).</div>
                    </Col>
                </Row>
            ) : null}
        </div>
    )
}
